import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import {
  ExpandMore,
  ChevronRight,
  Folder,
  FolderOpen,
  Home,
} from '@mui/icons-material';
import { useCacheStore } from '../hooks/data/useCacheStore';
import { Category } from '../types/bookmarks';

/**
 * Returns the set of ancestor category ids for a given category id (walking up
 * via parent_id), NOT including the category itself. Used to auto-expand the
 * path to the current location.
 */
function computeAncestorIds(
  categoryId: string | null,
  categories: Category[],
): Set<string> {
  const ids = new Set<string>();
  if (!categoryId) return ids;
  const byId = new Map(categories.map((c) => [c.category_id, c]));
  let current = byId.get(categoryId);
  while (current && current.parent_id) {
    ids.add(current.parent_id);
    current = byId.get(current.parent_id);
  }
  return ids;
}

interface CategoryPickerProps {
  open: boolean;
  title: string;
  disabledIds: Set<string>;
  currentLocationId?: string | null;
  onSelect: (categoryId: string | null) => void;
  onCancel: () => void;
  /**
   * Optional explicit category list. When provided, the picker renders this
   * tree instead of reading bookmark categories from the cache. Used by the
   * Notes app to move note categories/notes (NoteCategory is structurally
   * identical to Category).
   */
  categories?: Category[];
}

interface TreeNode {
  category: Category;
  children: TreeNode[];
}

/**
 * Builds a hierarchical tree from a flat category array.
 */
function buildTree(categories: Category[]): TreeNode[] {
  const childrenMap = new Map<string | null, Category[]>();

  for (const cat of categories) {
    const parentKey = cat.parent_id;
    if (!childrenMap.has(parentKey)) {
      childrenMap.set(parentKey, []);
    }
    childrenMap.get(parentKey)!.push(cat);
  }

  const buildNodes = (parentId: string | null): TreeNode[] => {
    const children = childrenMap.get(parentId) || [];
    return children.map((cat) => ({
      category: cat,
      children: buildNodes(cat.category_id),
    }));
  };

  return buildNodes(null);
}

interface CategoryTreeNodePickerProps {
  node: TreeNode;
  depth: number;
  disabledIds: Set<string>;
  currentLocationId?: string | null;
  /** Ancestor ids of the current location — nodes in this set start expanded. */
  defaultExpandedIds: Set<string>;
  /** Ref attached to the row that matches currentLocationId, for scroll-into-view. */
  currentRef?: React.Ref<HTMLDivElement>;
  onSelect: (categoryId: string) => void;
}

const CategoryTreeNodePicker: React.FC<CategoryTreeNodePickerProps> = ({
  node,
  depth,
  disabledIds,
  currentLocationId,
  defaultExpandedIds,
  currentRef,
  onSelect,
}) => {
  const hasChildren = node.children.length > 0;
  const isDisabled = disabledIds.has(node.category.category_id);
  const isCurrent = currentLocationId === node.category.category_id;
  const [expanded, setExpanded] = useState(
    defaultExpandedIds.has(node.category.category_id),
  );

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  const handleSelect = () => {
    if (!isDisabled) {
      onSelect(node.category.category_id);
    }
  };

  return (
    <>
      <ListItemButton
        ref={isCurrent ? currentRef : undefined}
        onClick={handleSelect}
        // Not using the MUI `disabled` prop: it would also disable the nested
        // expand chevron, making it impossible to drill into a disabled
        // category's subcategories (e.g. move a bookmark from a folder into its
        // own subfolder). Instead we mute the row and no-op selection while
        // keeping the chevron clickable.
        aria-disabled={isDisabled}
        sx={{
          pl: 2 + depth * 2,
          cursor: isDisabled ? 'default' : 'pointer',
          bgcolor: isCurrent ? 'action.selected' : undefined,
          '& .picker-label': { opacity: isDisabled ? 0.5 : 1 },
          '&:hover': {
            bgcolor: isDisabled
              ? isCurrent
                ? 'action.selected'
                : 'transparent'
              : isCurrent
              ? 'action.selected'
              : undefined,
          },
        }}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={handleToggleExpand}
            sx={{ mr: 0.5 }}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? (
              <ExpandMore fontSize="small" />
            ) : (
              <ChevronRight fontSize="small" />
            )}
          </IconButton>
        ) : (
          <Box sx={{ width: 34, mr: 0.5 }} />
        )}
        <ListItemIcon className="picker-label" sx={{ minWidth: 32 }}>
          {expanded && hasChildren ? (
            <FolderOpen fontSize="small" />
          ) : (
            <Folder fontSize="small" />
          )}
        </ListItemIcon>
        <ListItemText
          className="picker-label"
          primary={
            <Typography
              variant="body2"
              noWrap
              component="span"
              sx={{ fontWeight: isCurrent ? 700 : 400 }}
            >
              {node.category.category_name}
              {isCurrent && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ ml: 1, color: 'text.secondary' }}
                >
                  (current)
                </Typography>
              )}
            </Typography>
          }
        />
      </ListItemButton>

      {hasChildren && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {node.children.map((child) => (
              <CategoryTreeNodePicker
                key={child.category.category_id}
                node={child}
                depth={depth + 1}
                disabledIds={disabledIds}
                currentLocationId={currentLocationId}
                defaultExpandedIds={defaultExpandedIds}
                currentRef={currentRef}
                onSelect={onSelect}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

export const CategoryPicker: React.FC<CategoryPickerProps> = ({
  open,
  title,
  disabledIds,
  currentLocationId,
  onSelect,
  onCancel,
  categories: categoriesProp,
}) => {
  const cacheStore = useCacheStore();
  const currentRef = useRef<HTMLDivElement>(null);

  const { tree, defaultExpandedIds } = useMemo(() => {
    const categories = categoriesProp ?? cacheStore.getCategories();
    if (!categories) {
      return { tree: [] as TreeNode[], defaultExpandedIds: new Set<string>() };
    }
    return {
      tree: buildTree(categories),
      defaultExpandedIds: computeAncestorIds(
        currentLocationId ?? null,
        categories,
      ),
    };
  }, [cacheStore, categoriesProp, open, currentLocationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll the current location into view when the dialog opens.
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => {
      currentRef.current?.scrollIntoView({ block: 'center' });
    }, 150);
    return () => window.clearTimeout(id);
  }, [open]);

  const isRootCurrent = currentLocationId === null;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <List component="nav" dense>
          {/* Root option */}
          <ListItemButton
            onClick={() => onSelect(null)}
            sx={{
              bgcolor: isRootCurrent ? 'action.selected' : undefined,
            }}
          >
            <Box sx={{ width: 34, mr: 0.5 }} />
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Home fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ fontWeight: isRootCurrent ? 700 : 400 }}
                >
                  Root
                  {isRootCurrent && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ ml: 1, color: 'text.secondary' }}
                    >
                      (current)
                    </Typography>
                  )}
                </Typography>
              }
            />
          </ListItemButton>

          {/* Category tree */}
          {tree.map((node) => (
            <CategoryTreeNodePicker
              key={node.category.category_id}
              node={node}
              depth={0}
              disabledIds={disabledIds}
              currentLocationId={currentLocationId}
              defaultExpandedIds={defaultExpandedIds}
              currentRef={currentRef}
              onSelect={onSelect}
            />
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
